import { useCallback, useRef, useState, SetStateAction, Dispatch } from 'react';

const isFunction = <S>(setStateAction: SetStateAction<S>): setStateAction is (prevState: S) => S =>
    typeof setStateAction === 'function';

type ReadOnlyRefObject<T> = {
    readonly current: T;
};

type UseStateRef = {
    <S>(initialState: S | (() => S)): [S, Dispatch<SetStateAction<S>>, ReadOnlyRefObject<S>];
    <S = undefined>(): [S | undefined, Dispatch<SetStateAction<S | undefined>>, ReadOnlyRefObject<S | undefined>];
};

/**
 * This hook was added to help with useState inside vanilla callbacks, using a useState value inside a callback
 *     will lead to the value staying the same as when component was rendered.
 *
 *     Exposing this value using the reference.current will allow the callback to access the latest value
 * @param initialState
 */
const useStateRef: UseStateRef = <S>(initialState?: S | (() => S)) => {
    const [state, setState] = useState(initialState);
    const ref = useRef(state);

    const dispatch: typeof setState = useCallback((setStateAction: any) => {
        ref.current = isFunction(setStateAction) ? setStateAction(ref.current) : setStateAction;

        setState(ref.current);
    }, []);

    return [state, dispatch, ref];
};

export default useStateRef;
